import { createAsyncThunk, createEntityAdapter, createSlice, Dictionary } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { ReduxEntity } from 'Douleutaras/types';
import { RootState } from 'Douleutaras/redux/store';
import { CategoryForm } from 'Douleutaras/redux/data/v2/categoryForm/types';

const categoryFormAdapter = createEntityAdapter<CategoryForm>({
  selectId: item => item.category.id
});

const CATEGORY_FORM_ENDPOINT = 'api/public/category_form';

interface CategoryFormParams {
  forJob?: string;
  isPrice?: boolean;
  categoryId: string;
  seller?: boolean; // get questions as seller or buyer
}

export const getCategoryForm = createAsyncThunk<{ result: CategoryForm; meta: Dictionary<number> }, CategoryFormParams>(
  'categoryForm/get',
  async ({ categoryId, forJob, isPrice, seller = true }, api) => {
    const additionalGetParams = {
      seller: undefined,
      for_job: forJob,
      is_price: isPrice
    };

    if (seller) {
      additionalGetParams.seller = true;
    }

    const response = await makeRequest(`${CATEGORY_FORM_ENDPOINT}/${categoryId}`, {
      method: 'GET',
      additionalGetParams
    })(api);

    // This one differentiates the current listing based on tab/page
    const endpointIdentifier = forJob ? `${CATEGORY_FORM_ENDPOINT}/${categoryId}/${forJob}` : `${CATEGORY_FORM_ENDPOINT}/${categoryId}`;

    return {
      result: response?.data,
      meta: {
        [endpointIdentifier]: response.data.id
      }
    };
  }
);

export const categoryFormSlice = createSlice({
  name: 'categoryForm',
  initialState: categoryFormAdapter.getInitialState({
    meta: {},
    loading: undefined
  } as ReduxEntity<CategoryForm>),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getCategoryForm.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getCategoryForm.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(getCategoryForm.fulfilled, (state, action) => {
      categoryFormAdapter.upsertOne(state, action.payload.result);
      state.meta = { ...state.meta, ...action.payload.meta };
      state.loading = false;
    });
  }
});

export const categoryFormSelectors = categoryFormAdapter.getSelectors((state: RootState) => state.categoryForm);
